import { apiEndpoints } from "./config";
import { WEBCHAT_CONFIG } from "./components/webchatConfig";
import apiClient from "./api/apiClient";

export const customerAPI = {
    // Get Monthly Usage
    getMonthlyUsage: async (token, month, userId) => {
        try {
            // month format: YYYY-MM
            let queryString = month ? `?month=${month}` : "";
            if (userId) {
                queryString += queryString ? `&user_id=${userId}` : `?user_id=${userId}`;
            }

            const response = await apiClient.get(
                `${apiEndpoints.monthlyUsage}${queryString}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                }
            );

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Get Credits (Total/Remaining)
    getCredits: async (token, userId) => {
        try {
            const query = userId ? `?user_id=${userId}` : "";
            const response = await apiClient.get(`${apiEndpoints.credits}${query}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Deduct Credit (Per Chat Message)
    deductCredit: async (token) => {
        try {
            const response = await apiClient.post(apiEndpoints.deductChat, {}, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            return { success: true, data: response.data };
        } catch (error) {
            // Pass full error object if available
            if (error.response && error.response.data) {
                const data = error.response.data;
                const errorData = {
                    message: data.message || "Failed to deduct credit",
                    code: data.code,
                    status: data.status,
                    remainingCredits: data.remainingCredits,
                    requiredCredits: data.requiredCredits
                };
                return { success: false, error: errorData };
            }

            return { success: false, error: { message: error.message } };
        }
    },

    // Track File Upload
    trackFile: async (token, fileData) => {
        try {
            const response = await apiClient.post(apiEndpoints.trackingFile, fileData, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Get Stored Files (Tracking History)
    getStoredFiles: async (token, userId) => {
        try {
            const queryString = userId ? `?user_id=${userId}` : "";
            const response = await apiClient.get(`${apiEndpoints.trackingFile}${queryString}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Create Payment Order
    createOrder: async (token, plan, gateway, planVersionId, currency) => {
        try {
            const response = await apiClient.post(apiEndpoints.createOrder, {
                planVersionId: planVersionId,
                gateway: gateway,
                currency: currency
            }, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Verify Payment
    verifyPayment: async (token, paymentData) => {
        try {
            const response = await apiClient.post(
                apiEndpoints.verifyPayment,
                paymentData,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                }
            );

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    getPaymentHistory: async (token) => {
        try {
            const response = await apiClient.get(apiEndpoints.paymentHistory, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Get Infinium Plans
    getPlans: async (token) => {
        try {
            const response = await apiClient.get(apiEndpoints.getInfiniumPlans, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Get Active Subscription
    getActiveSubscription: async (token) => {
        try {
            const response = await apiClient.get(apiEndpoints.activeSubscription, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Renew Subscription
    renewSubscription: async (token, planVersionId, gateway, currency) => {
        try {
            const response = await apiClient.post(apiEndpoints.renewSubscription, {
                planVersionId,
                gateway,
                currency
            }, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Upgrade Subscription (Prorated)
    upgradeSubscription: async (token, newVersionId, gateway, currency) => {
        try {
            const response = await apiClient.post(apiEndpoints.proratedPayment, {
                newVersionId,
                gateway,
                currency
            }, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },


    // Send transactional email (requires customer JWT)
    sendEmail: async (emailData) => {
        try {
            const {
                uuid,
                to,
                subject,
                html,
                text,
                cc,
                bcc,
                replyTo,
                sender,
                attachments,
            } = emailData;

            const payload = {
                uuid: uuid || WEBCHAT_CONFIG.uuid,
                to,
                subject,
            };

            if (html !== undefined) payload.html = html;
            if (text !== undefined) payload.text = text;
            if (cc !== undefined) payload.cc = cc;
            if (bcc !== undefined) payload.bcc = bcc;
            if (replyTo !== undefined) payload.replyTo = replyTo;
            if (sender !== undefined) payload.sender = sender;
            if (attachments !== undefined) payload.attachments = attachments;

            const response = await apiClient.post(apiEndpoints.sendEmail, payload);

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },

    // Assign User (Link Chat User to Customer)
    assignUser: async (companyCustomerPriEmail, companyCustomerId, userId, userToken) => {
        try {
            // This one used getHeaders() (auth required) but used `token`.
            // Wait, previous code:
            // headers: getHeaders(), body: ... user_token: userToken ...
            // getHeaders() uses api.authToken by default if no userToken arg passed to it.
            // assignUser params: ..., userId, userToken.
            // But getHeaders() was called with NO arguments. So it used api.authToken.

            const response = await apiClient.post(apiEndpoints.assignUser, {
                companyCustomerPriEmail,
                companyCustomerId,
                user_id: userId,
                user_token: userToken
            });

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || error.message
            };
        }
    },
};
